import { faShuffle } from '@fortawesome/pro-regular-svg-icons';
import { fetchOnlineBookingStaffNextAvailableDateThunk } from 'actions/onlineBooking/OnlineBookingActions';
import { RadioAvatarCard } from 'components/Form/RadioAvatarCard/RadioAvatarCard';
import { StaffRadioCard } from 'components/Staff/RadioCard/StaffRadioCard';
import { useMarketplace } from 'hooks/useMarketplace';
import moment from 'moment-timezone';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    getOnlineBookingAvailableStaffs,
    getOnlineBookingEarliestNextAvailableDate
} from 'reducers/onlineBooking/OnlineBookingSelectors';

import classes from './StaffSelector.module.scss';

const DATE_FORMAT = 'MMM D, YYYY';

interface StaffSelectorProps {
    selectedStaffId: number | null;
    onSelected: (staffId: number | null) => void;
}

export const StaffSelector: React.FC<StaffSelectorProps> = ({ selectedStaffId, onSelected }) => {
    const dispatch = useDispatch();
    const marketplace = useMarketplace();

    const availableStaffs = useSelector(getOnlineBookingAvailableStaffs);
    const earliestNextAvailableDate = useSelector(getOnlineBookingEarliestNextAvailableDate);

    const today = moment().tz(String(marketplace.timeZone));

    const nextAvailableDateForNoPreference = React.useMemo(() => {
        if (!earliestNextAvailableDate) {
            return ` `;
        }

        return (
            <>
                Available: <b>{earliestNextAvailableDate.format(DATE_FORMAT)}</b>
            </>
        );
    }, [earliestNextAvailableDate]);

    const changeStaffHandler = (staffId: number | null) => {
        onSelected(staffId);
    };

    React.useEffect(() => {
        const staffIds = availableStaffs.flatMap(item => item.staff).map(staff => staff.id);

        dispatch(fetchOnlineBookingStaffNextAvailableDateThunk(today, staffIds));
        // eslint-disable-next-line
    }, []);

    return (
        <div className={classes.root}>
            <RadioAvatarCard
                icon={faShuffle}
                label="No preference"
                checked={selectedStaffId === null}
                description={nextAvailableDateForNoPreference}
                onChange={() => changeStaffHandler(null)}
            />

            {availableStaffs.map(({ staff, loading, busyForWeeks, nextAvailableAt }) => (
                <StaffRadioCard
                    key={staff.id}
                    staff={staff}
                    loading={loading}
                    dateFormat={DATE_FORMAT}
                    busyForWeeks={busyForWeeks}
                    availableAt={nextAvailableAt?.date}
                    selected={selectedStaffId === staff.id}
                    onSelected={() => changeStaffHandler(staff.id)}
                />
            ))}
        </div>
    );
};
